import { PencilSimple, Plus } from '@phosphor-icons/react'
import { Button, Input } from 'antd'
import { useCallback, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { MeasureType } from '@/domain/model/measures'
import { useClickOutside } from '@/hooks/useClickOutside'
import useSearchParams from '@/hooks/useSearchParams'
import { useUpdateAnswerForMeasureQuestion } from '@/queries/measures/useUpdateAnswerForMeasureQuestion'
import Tooltip from '@/ui/atoms/Tooltip'

export const NotesByAssessment = ({ id, notes }: MeasureType) => {
  const { clientId = '' } = useParams<{ clientId?: string }>()
  const searchParams = useSearchParams()
  const formId = searchParams.get('formId') || ''
  const questionId = searchParams.get('question') || ''

  const containerRef = useRef<HTMLDivElement | null>(null)
  const editorContainerRef = useRef<HTMLDivElement | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [description, setDescription] = useState<string | null>(notes)

  const { updateAnswerForMeasureQuestion, isLoading } =
    useUpdateAnswerForMeasureQuestion({
      clientId,
      formId,
      questionId,
    })

  const buttonProps = {
    title: notes ? 'Edit Note' : 'Add Note',
    icon: notes ? <PencilSimple /> : <Plus />,
  }

  const handleOpenEditor = () => {
    const container = containerRef.current
    const editorContainer = editorContainerRef.current

    if (!container || !editorContainer) return
    const rect = container.getBoundingClientRect()
    const top = rect.top + window.scrollY
    const left = rect.left + window.scrollX

    Object.assign(editorContainer.style, {
      display: 'flex',
      width: `${rect.width + 20}px`,
      top: `${top - 12}px`,
      left: `${left - 12}px`,
    })
    setIsOpen(true)
  }

  const handleCloseEditor = () => {
    const editorContainer = editorContainerRef.current

    if (!editorContainer) return
    Object.assign(editorContainer.style, {
      display: 'none',
      width: `0px`,
    })
    setIsOpen(false)
  }

  const onSubmit = useCallback(
    async (clickOutside?: boolean) => {
      clickOutside && handleCloseEditor()
      try {
        const data = {
          notes: description,
          id,
        }
        await updateAnswerForMeasureQuestion(data)
        handleCloseEditor()
      } catch (error) {
        toast.error('Error trying to save notes')
      }
    },
    [description, id, updateAnswerForMeasureQuestion]
  )

  useClickOutside(editorContainerRef, () => isOpen && onSubmit(true))

  return (
    <>
      <div className="relative flex h-full w-full gap-2" ref={containerRef}>
        <p className="line-clamp-2 w-full" onClick={handleOpenEditor}>
          {notes}
        </p>
        <Tooltip mouseEnterDelay={0.4} title={buttonProps.title}>
          <Button
            className="!text-gray-500 !outline-none hover:!text-gray-500/60"
            onClick={handleOpenEditor}
            type="link"
          >
            {buttonProps.icon}
          </Button>
        </Tooltip>
      </div>
      {createPortal(
        <div
          className="absolute left-0 top-0 z-[2] hidden min-h-32 w-0 flex-col gap-2 rounded bg-white p-2 shadow"
          ref={editorContainerRef}
        >
          {isOpen ? (
            <>
              <Input.TextArea
                className="!h-32 w-full"
                defaultValue={description || undefined}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Typing something..."
              />
              <div className="flex justify-end">
                <Button
                  className="h-9 !outline-none"
                  disabled={isLoading}
                  onClick={handleCloseEditor}
                  type="link"
                >
                  Cancel
                </Button>
                <Button
                  className="h-9"
                  loading={isLoading}
                  onClick={() => onSubmit()}
                  type="primary"
                >
                  Save
                </Button>
              </div>
            </>
          ) : null}
        </div>,
        document.body
      )}
    </>
  )
}
